import type { ColumnPreview } from './use-template-prerequisites';

export type AutomationDatabaseSurfaceRole = 'input' | 'output' | 'database';
export type AutomationDatabaseBindingMode = 'create_new' | 'use_existing';
export type AutomationDatabaseColumnBindingAction =
  | 'map_existing'
  | 'create_column';

export interface AutomationDatabaseColumnBinding {
  templateColumnId: string;
  templateColumnName: string;
  templateColumnType: string;
  action: AutomationDatabaseColumnBindingAction;
  targetColumnId?: string;
  targetColumnName?: string;
  targetColumnType?: string;
}

export interface AutomationDatabaseSurfaceBinding {
  databaseKey: string;
  databaseLabel: string;
  databaseRole: AutomationDatabaseSurfaceRole;
  databaseIndex: number;
  mode: AutomationDatabaseBindingMode;
  targetDocId?: string;
  targetDatabaseBlockId?: string;
  targetDatabaseName?: string;
  columns: AutomationDatabaseColumnBinding[];
}

export interface AutomationDatabaseBindings {
  schemaVersion: 1;
  surfaces: AutomationDatabaseSurfaceBinding[];
}

export interface TemplateDatabaseSurface {
  databaseKey: string;
  databaseLabel: string;
  databaseRole: AutomationDatabaseSurfaceRole;
  databaseIndex: number;
  columns: ColumnPreview[];
}

export function buildTemplateDatabaseSurfaces(
  columns: readonly ColumnPreview[]
): TemplateDatabaseSurface[] {
  const groups = new Map<string, TemplateDatabaseSurface>();

  for (const column of columns) {
    const databaseRole = normalizeDatabaseRole(column.databaseRole);
    const databaseIndex = column.databaseIndex ?? 0;
    const databaseKey =
      column.databaseKey ?? `${databaseRole}.${databaseIndex}`;
    const existing = groups.get(databaseKey);
    if (existing) {
      existing.columns.push(column);
      continue;
    }

    groups.set(databaseKey, {
      databaseKey,
      databaseLabel:
        column.databaseLabel ??
        defaultDatabaseLabel(databaseRole, databaseIndex),
      databaseRole,
      databaseIndex,
      columns: [column],
    });
  }

  return Array.from(groups.values());
}

export function createAutomationDatabaseBindings(
  columns: readonly ColumnPreview[],
  targetDocId: string | undefined
): AutomationDatabaseBindings {
  return {
    schemaVersion: 1,
    surfaces: buildTemplateDatabaseSurfaces(columns).map(surface => ({
      databaseKey: surface.databaseKey,
      databaseLabel: surface.databaseLabel,
      databaseRole: surface.databaseRole,
      databaseIndex: surface.databaseIndex,
      mode: 'create_new',
      ...(targetDocId ? { targetDocId } : {}),
      columns: surface.columns.map(column => ({
        templateColumnId: column.id,
        templateColumnName: column.name,
        templateColumnType: column.type,
        action: 'create_column',
      })),
    })),
  };
}

export function updateAutomationDatabaseBindingTargetDoc(
  bindings: AutomationDatabaseBindings,
  targetDocId: string | undefined
): AutomationDatabaseBindings {
  return {
    ...bindings,
    surfaces: bindings.surfaces.map(surface => ({
      ...surface,
      ...(targetDocId ? { targetDocId } : {}),
      ...(!targetDocId
        ? {
            targetDocId: undefined,
            targetDatabaseBlockId: undefined,
            targetDatabaseName: undefined,
            mode: 'create_new' as const,
            columns: surface.columns.map(column => ({
              ...column,
              action: 'create_column' as const,
              targetColumnId: undefined,
              targetColumnName: undefined,
              targetColumnType: undefined,
            })),
          }
        : {}),
    })),
  };
}

export function hasExistingDatabaseBindings(
  bindings: AutomationDatabaseBindings | undefined
): boolean {
  return (
    bindings?.surfaces.some(surface => surface.mode === 'use_existing') ?? false
  );
}

export function getAutomationDatabaseCreateFlags(
  bindings: AutomationDatabaseBindings | undefined
): { createInputDbs: boolean; createOutputDbs: boolean } {
  if (!bindings) return { createInputDbs: true, createOutputDbs: true };
  if (bindings.surfaces.length === 0) {
    return { createInputDbs: true, createOutputDbs: true };
  }
  return {
    createInputDbs: bindings.surfaces.some(
      surface =>
        surface.databaseRole === 'input' && surface.mode === 'create_new'
    ),
    createOutputDbs: bindings.surfaces.some(
      surface =>
        surface.databaseRole === 'output' && surface.mode === 'create_new'
    ),
  };
}

export function buildColumnMappings(
  bindings: AutomationDatabaseBindings | undefined
): Record<string, string> {
  const mappings: Record<string, string> = {};
  for (const surface of bindings?.surfaces ?? []) {
    if (surface.mode !== 'use_existing') continue;
    for (const column of surface.columns) {
      if (!column.targetColumnId) continue;
      mappings[column.templateColumnId] = column.targetColumnId;
    }
  }
  return mappings;
}

function normalizeDatabaseRole(
  role: ColumnPreview['databaseRole'] | undefined
): AutomationDatabaseSurfaceRole {
  if (role === 'input' || role === 'output') return role;
  return 'database';
}

function defaultDatabaseLabel(
  role: AutomationDatabaseSurfaceRole,
  index: number
): string {
  if (role === 'input') return 'Input database';
  if (role === 'output') {
    return index > 0 ? `Output database ${index + 1}` : 'Output database';
  }
  return 'Database';
}
